/**
 * `/state`'s own words: the approved prototype's, character for character
 * (`_design/state-prototype/brainspot-patterns-styled.html`; ruling R72, owner
 * 2026-10-01). Enrico reviews the science wording on dev after deploy, so this
 * file is the ONE place that wording lives and the one place to change it.
 *
 * Words that belong to a reading — the corner words, the axis names and ends,
 * the state word and line, a reason's sentence — are never here: they are the
 * payload's `state_map.display` (or `/home.display`) and are rendered as sent.
 *
 * `SAMPLE_*` strings describe sample data. They are shown only in a build whose
 * API is the mock fixtures (`VITE_API_MOCKS`), where the data IS sample data;
 * a real build shows the stored readings and never calls them illustrative.
 */
export const COPY = {
  subtitle: 'Your readings, over time.',
  sample: 'Preview · sample data',
  mapHeading: 'Your recent readings',
  mapHelpLabel: 'About the state map',
  mapHelp: 'Each dot is one stored reading. Its vertical position is activation; its horizontal position is balance. Select a dot or date to explore that day.',
  research: 'Research only',
  researchHelp: 'This map is an exploratory research view, not a diagnosis or a measure of how well you are doing.',
  mapNote: 'Select a day. The map and timelines stay together.',
  datesLabel: 'Choose a reading date',
  selectedBrow: 'Selected reading',
  previous: 'Previous reading',
  next: 'Next reading',
  activationHelpLabel: 'What activation means',
  activationHelp: 'The stored activation axis places this reading relative to your own baseline. Above zero is more activated; below zero is less activated. Higher is not automatically better.',
  balanceHelpLabel: 'What balance means',
  balanceHelp: 'The stored balance axis uses the map’s exploratory labels: pulling back on the left and leaning in on the right. It is separate from the rolling balance score.',
  reported: 'What you reported',
  reportedHelpLabel: 'About your reported context',
  reportedHelp: 'Your own journal answers for this recording. They are shown beside the reading as context; the app does not infer that they caused it.',
  sleep: 'Sleep',
  level: 'Stress',
  caffeine: 'Caffeine',
  explore: 'Explore this reading',
  trendsHeading: 'Across these days',
  trendsHelpLabel: 'About these timelines',
  trendsHelp: 'These are stored values for individual readings, not a newly calculated trend score. Zero is the baseline reference for that reading. Missing values remain gaps; dates are selectable.',
  unavailable: 'Unavailable',
  reportBrowSample: 'Recording detail · sample values',
  reportBrowStored: 'Recording detail · stored values',
  close: 'Close',
  scoreSample: 'Stored score · illustrative',
  scoreStored: 'Stored score',
  scoreMissing: 'Unavailable for this reading',
  footerSample: 'Illustrative readings and journal entries · personal baselines can change.',
  footerStored: 'Stored readings and journal entries · personal baselines can change.',
  aboutSample: 'About this prototype',
  aboutSampleHelp: 'The product would show the backend’s stored labels, confidence, warnings, and baseline context. This prototype uses illustrative data to demonstrate the interaction.',
  noPosition: 'no map position',
  notReported: 'Not reported',
  noCheckIn: 'No check-in for this reading',
  loadMore: 'Load more recordings',
  locked: 'The map unlocks when your eyes closed baseline is confirmed.',
} as const

/** The four stored scores the prototype's detail panel shows, in its order, by `ui_scores` key. */
export const SCORE_KEYS = ['readiness', 'recovery', 'focus_stability', 'calm'] as const
